<template>
  <div class="login-wrapper">
    <div class="overlay" v-if="loading">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <div class="login-card fade-in-up">
      <div class="lang-switch-container">
        <button
          class="btn-lang"
          @click="langswap()"
          :title="
            langmode === 'ENG'
              ? 'Switch to Bahasa Malaysia'
              : 'Switch to English'
          "
        >
          <img
            v-if="langmode === 'ENG'"
            src="../assets/country_flags/en_US.svg"
            alt="English"
            class="flag-icon"
          />
          <img
            v-else
            src="../assets/country_flags/ms_MY.svg"
            alt="Bahasa Malaysia"
            class="flag-icon"
          />
          <span class="lang-code">{{ langmode }}</span>
        </button>
      </div>

      <div class="login-header text-center">
        <img
          src="../assets/ctrlytics-logo-black.png"
          alt="Logo"
          class="brand-logo"
        />
        <p class="brand-tagline">{{ lang.title_text }}</p>
      </div>


      <form @submit.prevent="login" class="login-form">
        <div class="form-group custom-input-group">
          <label class="form-label" for="username">{{ lang.username }}</label>
          <input
            type="text"
            class="form-control custom-input"
            id="username"
            :placeholder="lang.username_hint"
            v-model="username"
            required
          />
        </div>

        <div class="form-group custom-input-group">
          <label class="form-label" for="password">{{ lang.password }}</label>
          <input
            type="password"
            class="form-control custom-input"
            id="password"
            :placeholder="lang.password_hint"
            v-model="password"
            required
          />
        </div>

        <div class="text-center action-area">
          <button type="submit" class="btn btn-primary login-btn">
            {{ lang.login_button }}
          </button>

          <transition name="shake">
            <div v-if="tryagainWarning" class="alert-box error">
              {{ lang.login_warning }}
            </div>
          </transition>
        </div>
      </form>

      <div class="login-footer">
        <p class="text-muted">{{ lang.powered_by }}</p>
        <p class="version-text">v{{ appVersion }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import changelogData from "@/data/changelog.json";
import { resetSessionGuard } from "../api/sessionGuard";

export default {
  data() {
    return {
      username: "",
      password: "",
      loading: false,
      tryagainWarning: false,
      lang: [],
      langmode: "ENG",
      appVersion: changelogData[0]?.version || "0.0.0",
    };
  },
  methods: {
    login() {
      console.log("Logging in with:", this.username, this.password);
      this.loading = true;
      this.tryagainWarning = false;

      if (this.username && this.password) {
        let data = JSON.stringify({
          username: this.username,
          password: this.password,
          not_pos_indicator: true,
        });
        let config = {
          method: "post",
          maxBodyLength: Infinity,
          url: process.env.VUE_APP_BACKEND_URL + "/v1/login",
          headers: {
            "Content-Type": "application/json",
          },
          data: data,
        };

        axios
          .request(config)
          .then((response) => {
            console.log(JSON.stringify(response.data));
            if (response.data["200"] != undefined) {
              console.log("Login Successful");
              localStorage.token = response.data["200"];
              localStorage.currentUser = this.username;
              localStorage.role = response.data.role;
              localStorage.bearer_token = response.data.bearer_token;
              localStorage.isDemo = response.data.is_demo;
              localStorage.isBillDue = response.data.bill_due;
              localStorage.setItem("csrf_token", response.data.csrf_token);
              this.loading = false;
              // Re-arm the guard: this is an SPA, so after a sign-out it would otherwise
              // stay disarmed for the rest of the page's life.
              resetSessionGuard();
              this.$router.replace({ path: "/dashboard" });
            } else {
              this.loading = false;
              this.tryagainWarning = true;
            }
          })
          .catch((error) => {
            console.log(error);
            this.loading = false;
            this.tryagainWarning = true;
          });
      } else {
        this.loading = false;
        // Simple client-side validation fallback
        if (!this.username) console.log("Please fill in username");
        if (!this.password) console.log("Please fill in password");
      }
    },
    async langchange(val) {
      this.langmode = val;
      const response = await fetch("/login.json");
      const data = await response.json();
      this.lang = data[this.langmode];
    },
    langswap() {
      var langmode;
      if (this.langmode === "ENG") {
        langmode = "BM";
      } else {
        langmode = "ENG";
      }
      this.langchange(langmode);
    },
  },
  mounted() {
    this.langchange("ENG");
    // Clear only session-related data, preserving user preferences like 'WhatsNewLastSeen'
    // dismissedNotices MUST survive this clear. The old disclaimer modal reappeared on every
    // single login precisely because its flag did not.
    const keysToPersist = ["whatsNewLastSeenVersion", "dismissedNotices", "lang"];
    const savedValues = {};
    keysToPersist.forEach((key) => {
      savedValues[key] = localStorage.getItem(key);
    });

    localStorage.clear();

    keysToPersist.forEach((key) => {
      if (savedValues[key]) localStorage.setItem(key, savedValues[key]);
    });
  },
};
</script>

<style scoped>
@import "../fonts/fonts.css";


/* Keyframes for animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translate3d(0, 40px, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  10%,
  30%,
  50%,
  70%,
  90% {
    transform: translateX(-5px);
  }
  20%,
  40%,
  60%,
  80% {
    transform: translateX(5px);
  }
}

.login-wrapper {
  background-color: var(--primary-color);
  background: var(--primary-color);
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--spacing-md);
  font-family: var(--font-family-base);
}

.login-card {
  background-color: var(--surface-color);
  width: 100%;
  max-width: 450px;
  padding: 40px;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-lg);
  position: relative;
  z-index: 100;
}

/* Language Switcher Button */
.lang-switch-container {
  position: absolute;
  top: 20px;
  right: 20px;
}

.btn-lang {
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: 20px;
  padding: 6px 12px;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-lang:hover {
  background-color: var(--background-color);
  transform: translateY(-1px);
}

.flag-icon {
  width: 20px;
  height: 15px; /* Adjust aspect ratio as needed */
  object-fit: cover;
  border-radius: 2px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.lang-code {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-main);
}

.fade-in-up {
  animation: fadeInUp 0.8s ease-out;
}

.login-header {
  margin-bottom: 30px;
  /* Adjust margin to account for top button if needed, but absolute positioning handles it */
  margin-top: 20px;
}

.brand-logo {
  max-width: 70%;
  height: auto;
  max-height: 80px;
  object-fit: contain;
  /* Removed cursor pointer and hover effect since it's no longer clickable */
}

.brand-tagline {
  font-size: var(--font-size-base);
  color: var(--text-main);
  font-weight: 500;
  margin-top: var(--spacing-sm);
  opacity: 0.8;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.custom-input-group {
  text-align: left;
}

.form-label {
  font-size: var(--font-size-sm);
  font-weight: 600;
  color: var(--text-main);
  margin-bottom: var(--spacing-xs);
  display: block;
}

.custom-input {
  background-color: var(--background-color);
  border: 2px solid transparent;
  height: 48px;
  border-radius: var(--border-radius-md);
  padding: 0 16px;
  font-size: var(--font-size-base);
  color: var(--text-main);
  transition: all var(--transition-fast);
}

.custom-input:focus {
  background-color: var(--surface-color);
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(255, 214, 0, 0.2);
  outline: none;
}

.action-area {
  margin-top: var(--spacing-md);
}

.login-btn {
  width: 100%;
  height: 48px;
  border-radius: 50px;
  background-color: var(--text-main);
  color: var(--primary-color);
  font-weight: 700;
  font-size: var(--font-size-base);
  border: none;
  letter-spacing: 0.5px;
  transition: all var(--transition-normal);
  box-shadow: var(--shadow-sm);
}

.login-btn:hover {
  background-color: #000;
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.login-btn:active {
  transform: translateY(0);
}

.alert-box {
  margin-top: 15px;
  color: var(--error-color);
  font-weight: 600;
  font-size: var(--font-size-sm);
  padding: 10px;
  background: rgba(255, 82, 82, 0.1);
  border-radius: var(--border-radius-sm);
}

.login-footer {
  text-align: center;
  margin-top: 40px;
}

.login-footer p {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  margin-bottom: 4px;
}

.version-text {
  opacity: 0.5;
  font-size: 11px !important;
  font-weight: 500;
}

/* Loading Overlay */
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
  backdrop-filter: blur(5px);
}

.spinner-border {
  width: 3rem;
  height: 3rem;
  color: var(--primary-color) !important;
}

/* Responsive specific adjustments */
@media (max-width: 480px) {
  .login-card {
    padding: 24px;
    box-shadow: none;
    background: transparent;
  }

  .login-wrapper {
    background: var(
      --surface-color
    ); /* On very small screens, make it simple */
    align-items: start;
    padding-top: 60px;
  }

  .custom-input {
    background-color: var(--background-color);
    border: 1px solid var(--border-color);
  }
}
</style>
